import { Capacitor } from '@capacitor/core'
import { localDb } from '@/data/local/database'
import { remoteApiRequest, saveApiEndpoints } from '@/data/api/remote-api'
import { secureTokenStore } from '@/services/auth/secure-token-store'
import { getActiveIdentity, setActiveIdentity } from '@/services/identity/local-identity'
import type { SyncEntity } from '@/domain/types'

export interface SessionResult {
  userId: string
  workspaceId: string
  deviceId: string
  role: 'owner' | 'member'
  displayName?: string
  token?: string
}

async function rehomeTable<T extends SyncEntity>(
  values: T[],
  userId: string,
  workspaceId: string,
  deviceId: string,
) {
  const now = Date.now()
  return values.map(item => ({
    ...item,
    ownerUserId: userId,
    workspaceId,
    updatedAtMs: now,
    version: 0,
    ...('deviceId' in item ? { deviceId } : {}),
  }))
}

async function attachExistingCloudIdentity(result: SessionResult) {
  const previous = await getActiveIdentity()
  if (result.token) await secureTokenStore.setToken(result.token)
  const now = Date.now()
  const isDifferentUser = previous.userId !== result.userId

  // Different user logging in → clear old entity data to prevent data leak
  if (isDifferentUser) {
    await localDb.transaction('rw', [
      localDb.studySessions, localDb.notes, localDb.todos,
      localDb.recurringTodos, localDb.practiceRecords,
      localDb.inspirationTags, localDb.userSettings,
      localDb.pendingOperations, localDb.conflicts,
    ], async () => {
      await Promise.all([
        localDb.studySessions.clear(),
        localDb.notes.clear(),
        localDb.todos.clear(),
        localDb.recurringTodos.clear(),
        localDb.practiceRecords.clear(),
        localDb.inspirationTags.clear(),
        localDb.userSettings.clear(),
        localDb.pendingOperations.clear(),
        localDb.conflicts.clear(),
      ])
    })
  }

  await localDb.transaction('rw', [
    localDb.localUsers,
    localDb.localWorkspaces,
    localDb.workspaceMemberships,
    localDb.localDevices,
    localDb.pendingOperations,
    localDb.syncState,
    localDb.appMetadata,
  ], async () => {
    await localDb.localUsers.put({
      id: result.userId,
      displayName: result.displayName || '云端用户',
      role: result.role,
      createdAtMs: now,
      disabledAtMs: null,
    })
    await localDb.localWorkspaces.put({
      id: result.workspaceId,
      name: '我的学习空间',
      ownerUserId: result.userId,
      kind: 'private',
      createdAtMs: now,
    })
    await localDb.workspaceMemberships.put({
      workspaceId: result.workspaceId,
      userId: result.userId,
      role: 'owner',
      joinedAtMs: now,
    })
    await localDb.localDevices.put({
      id: result.deviceId,
      userId: result.userId,
      name: Capacitor.isNativePlatform() ? 'Android 设备' : '网页浏览器',
      platform: Capacitor.isNativePlatform() ? 'android' : 'web',
      createdAtMs: now,
      lastSeenAtMs: now,
      revokedAtMs: null,
    })
    if (previous.userId === result.userId && previous.workspaceId === result.workspaceId) {
      await localDb.pendingOperations
        .where('workspaceId')
        .equals(result.workspaceId)
        .modify({ deviceId: result.deviceId })
    }
    if (!await localDb.syncState.get(result.workspaceId)) {
      await localDb.syncState.put({
        workspaceId: result.workspaceId,
        lastSequence: 0,
        lastSuccessAtMs: null,
        lastAttemptAtMs: null,
        status: 'idle',
        errorMessage: null,
        serverTimeOffsetMs: 0,
        endpoint: null,
      })
    }
    await setActiveIdentity({
      userId: result.userId,
      workspaceId: result.workspaceId,
      deviceId: result.deviceId,
      authenticated: true,
    })
  })
  window.dispatchEvent(new CustomEvent('study-tool:sync-request'))
}

async function attachNewCloudIdentity(result: SessionResult) {
  const previous = await getActiveIdentity()
  if (result.token) await secureTokenStore.setToken(result.token)
  const now = Date.now()
  const [
    sessions, notes, todos, recurring, practices, tags, settings,
  ] = await Promise.all([
    localDb.studySessions.toArray(),
    localDb.notes.toArray(),
    localDb.todos.toArray(),
    localDb.recurringTodos.toArray(),
    localDb.practiceRecords.toArray(),
    localDb.inspirationTags.toArray(),
    localDb.userSettings.toArray(),
  ])
  const scoped = <T extends SyncEntity>(items: T[]) => items.filter(item => item.workspaceId === previous.workspaceId)
  const migrated = {
    sessions: await rehomeTable(scoped(sessions), result.userId, result.workspaceId, result.deviceId),
    notes: await rehomeTable(scoped(notes), result.userId, result.workspaceId, result.deviceId),
    todos: await rehomeTable(scoped(todos), result.userId, result.workspaceId, result.deviceId),
    recurring: await rehomeTable(scoped(recurring), result.userId, result.workspaceId, result.deviceId),
    practices: await rehomeTable(scoped(practices), result.userId, result.workspaceId, result.deviceId),
    tags: await rehomeTable(scoped(tags), result.userId, result.workspaceId, result.deviceId),
    settings: (await rehomeTable(scoped(settings), result.userId, result.workspaceId, result.deviceId))
      .map(item => ({ ...item, id: result.userId })),
  }
  await localDb.transaction('rw', [
    localDb.studySessions, localDb.notes, localDb.todos, localDb.recurringTodos,
    localDb.practiceRecords, localDb.inspirationTags, localDb.userSettings,
    localDb.localUsers, localDb.localWorkspaces, localDb.workspaceMemberships,
    localDb.localDevices, localDb.pendingOperations, localDb.syncState, localDb.appMetadata,
  ], async () => {
    const previousSettingsIds = scoped(settings)
      .map(item => item.id)
      .filter(id => id !== result.userId)
    if (previousSettingsIds.length > 0) {
      await localDb.userSettings.bulkDelete(previousSettingsIds)
    }
    await Promise.all([
      localDb.studySessions.bulkPut(migrated.sessions),
      localDb.notes.bulkPut(migrated.notes),
      localDb.todos.bulkPut(migrated.todos),
      localDb.recurringTodos.bulkPut(migrated.recurring),
      localDb.practiceRecords.bulkPut(migrated.practices),
      localDb.inspirationTags.bulkPut(migrated.tags),
      localDb.userSettings.bulkPut(migrated.settings),
    ])
    await localDb.localUsers.put({ id: result.userId, displayName: '云端用户', role: result.role, createdAtMs: now, disabledAtMs: null })
    await localDb.localWorkspaces.put({ id: result.workspaceId, name: '我的学习空间', ownerUserId: result.userId, kind: 'private', createdAtMs: now })
    await localDb.workspaceMemberships.put({ workspaceId: result.workspaceId, userId: result.userId, role: 'owner', joinedAtMs: now })
    await localDb.localDevices.put({
      id: result.deviceId,
      userId: result.userId,
      name: Capacitor.isNativePlatform() ? 'Android 设备' : '网页浏览器',
      platform: Capacitor.isNativePlatform() ? 'android' : 'web',
      createdAtMs: now,
      lastSeenAtMs: now,
      revokedAtMs: null,
    })
    await localDb.pendingOperations.clear()
    const groups: Array<[string, SyncEntity[]]> = [
      ['study_session', migrated.sessions],
      ['note', migrated.notes],
      ['todo', migrated.todos],
      ['recurring_todo', migrated.recurring],
      ['practice_record', migrated.practices],
      ['inspiration_tag', migrated.tags],
      ['user_settings', migrated.settings],
    ]
    for (const [entityType, items] of groups) {
      for (const item of items) {
        await localDb.pendingOperations.add({
          operationId: `op_${crypto.randomUUID()}`,
          deviceId: result.deviceId,
          workspaceId: result.workspaceId,
          entityType: entityType as never,
          entityId: item.id,
          action: 'create',
          baseVersion: null,
          clientUpdatedAtMs: item.updatedAtMs,
          payload: item as unknown as Record<string, unknown>,
          retryCount: 0,
          nextRetryAtMs: now,
          createdAtMs: now,
        })
      }
    }
    await localDb.syncState.put({
      workspaceId: result.workspaceId,
      lastSequence: 0,
      lastSuccessAtMs: null,
      lastAttemptAtMs: null,
      status: 'idle',
      errorMessage: null,
      serverTimeOffsetMs: 0,
      endpoint: null,
    })
    await setActiveIdentity({ userId: result.userId, workspaceId: result.workspaceId, deviceId: result.deviceId, authenticated: true })
  })
  window.dispatchEvent(new CustomEvent('study-tool:sync-request'))
}

async function sessionRequest(
  path: string,
  body: Record<string, unknown>,
  options: { bootstrapToken?: string; accountMode?: 'existing' | 'new' } = {},
) {
  const response = await remoteApiRequest(path, {
    method: 'POST',
    headers: {
      'X-Client-Platform': Capacitor.isNativePlatform() ? 'android' : 'web',
      ...(options.bootstrapToken ? { 'X-Bootstrap-Token': options.bootstrapToken } : {}),
    },
    body: JSON.stringify(body),
  })
  const data = await response.json() as SessionResult & { error?: string }
  if (!response.ok) throw new Error(data.error || `连接失败（HTTP ${response.status}）`)
  if (options.accountMode === 'existing') await attachExistingCloudIdentity(data)
  else await attachNewCloudIdentity(data)
  return data
}

export async function bootstrapCloud(endpoint: string, token: string, displayName: string) {
  saveApiEndpoints(endpoint)
  return sessionRequest('auth/bootstrap', {
    displayName,
    deviceName: Capacitor.isNativePlatform() ? 'Android 设备' : '网页浏览器',
    platform: Capacitor.isNativePlatform() ? 'android' : 'web',
  }, { bootstrapToken: token })
}

export async function redeemCloudInvite(endpoint: string, code: string, displayName: string) {
  saveApiEndpoints(endpoint)
  return sessionRequest('auth/redeem', {
    code,
    displayName,
    deviceName: Capacitor.isNativePlatform() ? 'Android 设备' : '网页浏览器',
    platform: Capacitor.isNativePlatform() ? 'android' : 'web',
  })
}

function accountDevice() {
  return {
    deviceName: Capacitor.isNativePlatform() ? 'Android 设备' : '网页浏览器',
    platform: Capacitor.isNativePlatform() ? 'android' : 'web',
  }
}

export async function loginCloud(username: string, password: string) {
  return sessionRequest('auth/login', {
    username,
    password,
    ...accountDevice(),
  }, { accountMode: 'existing' })
}

export async function registerCloud(username: string, password: string) {
  return sessionRequest('auth/register', {
    username,
    password,
    ...accountDevice(),
  }, { accountMode: 'new' })
}

export async function restoreCloudSession() {
  const response = await remoteApiRequest('auth/me')
  if (!response.ok) throw new Error(response.status === 401 ? '未登录' : `连接失败（HTTP ${response.status}）`)
  const data = await response.json() as {
    user: { id: string; displayName: string; role: 'owner' | 'member' }
    deviceId: string
    workspaces: Array<{ id: string }>
  }
  const workspace = data.workspaces[0]
  if (!data.user || !workspace) throw new Error('账号工作区不存在')
  const session: SessionResult = {
    userId: data.user.id,
    workspaceId: workspace.id,
    deviceId: data.deviceId,
    role: data.user.role,
    displayName: data.user.displayName,
  }
  await attachExistingCloudIdentity(session)
  return session
}

export async function logoutCloud() {
  try {
    await remoteApiRequest('auth/logout', { method: 'POST' })
  } finally {
    await secureTokenStore.removeToken()
    const identity = await getActiveIdentity()
    await setActiveIdentity({ ...identity, authenticated: false })
  }
}

export async function createCloudInvite() {
  const response = await remoteApiRequest('invites', {
    method: 'POST',
    body: JSON.stringify({ expiresInDays: 7, maxUses: 1 }),
  })
  const data = await response.json() as { code?: string; error?: string; expiresAtMs?: number }
  if (!response.ok) throw new Error(data.error || '邀请码创建失败')
  return data
}
